import React, { useEffect, useState } from 'react';
import { Layout, Typography, Table, Input, Tag, Select, Tabs } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import useCampaignsStore from '../../stores/useCampaignsStore';
import CampaignViewHeader from './shared/CampaignViewHeader';
import CampaignStatsCards from './shared/CampaignStatsCards';
import CampaignDetailsCard from './shared/CampaignDetailsCard';
import CampaignFlowSection from './shared/CampaignFlowSection';
import CampaignSchedulingDetails from './shared/CampaignSchedulingDetails';
import FlowEnrollmentBoard from '../flow-enrollment/FlowEnrollmentBoard';
import CampaignContactDrawer from './CampaignContactDrawer';
import PhoneDisplay from '../common/PhoneDisplay';
import { evaluateTCPA } from '../../utils/tcpa';
import useFlowEnrollmentStore from '../../stores/useFlowEnrollmentStore';
import useModuleGroupStore from '../../stores/useModuleGroupStore';

const { Content } = Layout;
const { Option } = Select;

const compactStyles = {
    page: {
        padding: '8px 12px 12px',
        background: 'var(--gh-gray-50, #FAFAFA)'
    },
    shell: {
        display: 'flex',
        flexDirection: 'column',
        gap: 6
    },
    panel: {
        background: '#fff',
        border: '1px solid var(--gh-gray-200, #E6E6E6)',
        borderRadius: 8
    },
    controlsRow: {
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        marginTop: 6,
        marginBottom: 6
    }
};

const getDetailedMessageStatus = (record, campaignStatus) => {
    if (!campaignStatus) return 'Not Sent';

    switch (record.message_status) {
        case 'delivered':
            return 'Confirmed';
        case 'accepted':
        case 'sent':
            return 'Sent';
        case 'expired':
            return 'Failed (Expired)';
        case 'undelivered':
            return 'Failed (Undelivered)';
        case 'failed':
            return String(record.provider_code || '') === '6' ? 'Failed (Spam)' : 'Failed (Unknown)';
        case null:
        case undefined:
            if (record.opted_out_date || String(record.agreement || '').toLowerCase() === 'no') {
                return 'Not Sent (Contact Opted Out)';
            }
            if (record.provider_id) {
                return 'Sent';
            }
            return 'Pending';
        default:
            return 'Failed (Unknown)';
    }
};

const getMessageTagTone = (label) => {
    if (label === 'Confirmed' || label === 'Sent') {
        return {
            color: 'var(--gh-primary-active, #2E7D32)',
            background: 'var(--gh-primary-tint, #E8F5E9)',
            border: 'var(--gh-primary-border, #A5D6A7)'
        };
    }
    if (label === 'Pending') {
        return {
            color: 'var(--gh-warning, #FFB300)',
            background: 'rgba(255,179,0,0.12)',
            border: 'rgba(255,179,0,0.28)'
        };
    }
    if (label.startsWith('Not Sent')) {
        return {
            color: 'var(--gh-warning, #FFB300)',
            background: 'rgba(255,179,0,0.12)',
            border: 'rgba(255,179,0,0.28)'
        };
    }
    if (label.startsWith('Failed')) {
        return {
            color: 'var(--gh-danger, #FF4D4F)',
            background: 'rgba(255,77,79,0.12)',
            border: 'rgba(255,77,79,0.28)'
        };
    }
    return {
        color: 'var(--gh-text-secondary, #607D8B)',
        background: 'var(--gh-gray-100, #F0F0F0)',
        border: 'var(--gh-gray-200, #E6E6E6)'
    };
};

const getTcpaStatus = (record) => {
    const optedInDate = record.opted_in_date || '';
    const optedOutDate = record.opted_out_date || '';
    const agreement = (record.agreement || '').toString();

    if (!optedInDate && !optedOutDate && !agreement) {
        return '--';
    }

    const tcpa = evaluateTCPA({ optedInDate, optedOutDate, agreement });
    return tcpa === 0 ? 'Opted In' : 'Opted Out';
};

const CampaignViewOngoing = ({ match, history }) => {
    const { campaignId } = match.params;
    const {
        campaignDetails,
        campaignContacts,
        contactsPagination,
        contactsFilters,
        contactsLoading,
        setContactsFilters,
        setContactsPage,
        fetchCampaignDetails,
        fetchCampaignContacts,
        startAutoRefresh,
        stopAutoRefresh
    } = useCampaignsStore();

    const [localSearch, setLocalSearch] = useState(contactsFilters.search);
    const [drawerContactId, setDrawerContactId] = useState(null);
    const [drawerEnrollment, setDrawerEnrollment] = useState(null);

    const { boardData } = useFlowEnrollmentStore();
    const { modules } = useModuleGroupStore();

    const boardKey = campaignDetails?.modulegroup_id
        ? (campaignId ? `${campaignDetails.modulegroup_id}:${campaignId}` : campaignDetails.modulegroup_id)
        : null;
    const enrollments = boardKey ? (boardData[boardKey] || []) : [];

    // Sync drawer enrollment from store when board data changes (keeps drawer reactive after move/drop)
    useEffect(() => {
        if (!drawerEnrollment?.id) return;
        const fresh = enrollments.find((e) => e.id === drawerEnrollment.id);
        if (fresh && (fresh.current_module_id !== drawerEnrollment.current_module_id || fresh.status !== drawerEnrollment.status)) {
            setDrawerEnrollment(fresh);
        }
    }, [enrollments]);

    const handleContactRowClick = (record) => {
        const enrollment = enrollments.find((e) => e.contact_id === record.contact_id);
        setDrawerEnrollment(enrollment || null);
        setDrawerContactId(record.contact_id);
    };

    const handleEnrollmentCardClick = (enrollment) => {
        setDrawerEnrollment(enrollment);
        setDrawerContactId(enrollment.contact_id);
    };

    const handleDrawerClose = () => {
        setDrawerContactId(null);
        setDrawerEnrollment(null);
    };

    useEffect(() => {
        if (campaignId) {
            fetchCampaignContacts(campaignId);
            startAutoRefresh(campaignId, 15000);
        }
        return () => stopAutoRefresh();
    }, [campaignId]);

    // Debounced search
    useEffect(() => {
        setLocalSearch(contactsFilters.search);
    }, [contactsFilters.search]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (localSearch !== contactsFilters.search) {
                setContactsFilters({ search: localSearch });
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [localSearch]);

    const handleTableChange = (pag, filters, sorter) => {
        if (pag.current !== contactsPagination.page || pag.pageSize !== contactsPagination.pageSize) {
            setContactsPage(pag.current, pag.pageSize);
        }
        if (sorter.field && sorter.order) {
            const order = sorter.order === 'ascend' ? 'asc' : 'desc';
            if (sorter.field !== contactsFilters.sort || order !== contactsFilters.order) {
                setContactsFilters({ sort: sorter.field, order });
            }
        }
    };

    if (!campaignDetails) return null;

    const { stats = {} } = campaignDetails;
    const totalContacts = campaignDetails.contact_count_at_schedule || stats.total || 0;
    const delivered = (stats.accepted || 0) + (stats.delivered || 0);
    const failed = (stats.failed || 0) + (stats.expired || 0);
    const pending = Math.max(totalContacts - (delivered + failed), 0);

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: true,
            width: 260,
            ellipsis: true,
            render: (text, record) => (
                <a
                    className="gh-table-link"
                    onClick={(e) => { e.stopPropagation(); handleContactRowClick(record); }}
                    title={text}
                    style={{
                        cursor: 'pointer',
                        display: 'inline-block',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        verticalAlign: 'middle'
                    }}
                >
                    {text}
                </a>
            )
        },
        {
            title: 'Mobile',
            dataIndex: 'mobile',
            key: 'mobile',
            sorter: true,
            render: (text) => <PhoneDisplay value={text} />
        },
        {
            title: 'TCPA Status',
            key: 'tcpa',
            render: (_, record) => {
                const tcpa = getTcpaStatus(record);
                if (tcpa === '--') return '--';
                return <Tag style={{ borderRadius: 999, marginRight: 0 }}>{tcpa}</Tag>;
            }
        },
        {
            title: 'Message Status',
            dataIndex: 'message_status',
            key: 'status',
            sorter: true,
            render: (_, record) => {
                const label = getDetailedMessageStatus(record, true);
                const tone = getMessageTagTone(label);
                return (
                    <Tag
                        style={{
                            color: tone.color,
                            background: tone.background,
                            borderColor: tone.border,
                            borderRadius: 999,
                            fontSize: 11,
                            lineHeight: '18px',
                            paddingInline: 8,
                            marginRight: 0
                        }}
                    >
                        {label}
                    </Tag>
                );
            }
        }
    ];

    const liveIndicator = (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#52c41a',
                display: 'inline-block',
                animation: 'pulse 2s ease-in-out infinite',
                boxShadow: '0 0 4px #52c41a'
            }} />
            <span style={{ fontSize: 12, fontWeight: 500, color: '#52c41a' }}>Live</span>
        </span>
    );

    return (
        <Layout style={compactStyles.page}>
            <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.5; transform: scale(0.85); }
                }
            `}</style>
            <Content style={compactStyles.shell}>
                <CampaignViewHeader
                    campaignDetails={campaignDetails}
                    onBack={() => history.push('/campaign')}
                    onRefresh={() => {
                        fetchCampaignDetails(campaignId);
                        fetchCampaignContacts(campaignId);
                    }}
                    extraActions={liveIndicator}
                />

                <div style={{ ...compactStyles.panel, padding: '8px 10px' }}>
                    <CampaignStatsCards
                        stats={{ total: totalContacts, delivered, failed, pending }}
                        showProgress={true}
                        isLive={true}
                    />

                    <CampaignDetailsCard
                        campaignDetails={campaignDetails}
                        hideTemplate
                        hideCampaignStatus
                        hideMessage={!!campaignDetails.modulegroup_id}
                    />
                </div>

                {campaignDetails.modulegroup_id && (
                    <CampaignFlowSection
                        moduleGroupId={campaignDetails.modulegroup_id}
                        campaignState="ongoing"
                        defaultExpanded={false}
                        flowSnapshot={campaignDetails.flow_snapshot}
                        moduleResponseOverrides={campaignDetails.module_response_overrides}
                        noSlotsMessage={campaignDetails.no_slots_message}
                    />
                )}

                <CampaignSchedulingDetails
                    availabilitySettings={campaignDetails.availability_settings}
                    flowSnapshot={campaignDetails.flow_snapshot}
                    defaultExpanded={false}
                    schedulingUserId={campaignDetails.scheduling_user_id}
                    schedulingUserIds={campaignDetails.scheduling_user_ids}
                    schedulingStrategy={campaignDetails.scheduling_strategy}
                    schedulingUsers={campaignDetails.scheduling_users}
                    creatorName={campaignDetails.creator_name || campaignDetails.username}
                />

                <div style={{ ...compactStyles.panel, padding: '0 10px 8px' }}>
                    {campaignDetails.modulegroup_id ? (
                        <Tabs
                            size="small"
                            style={{ padding: '0 4px' }}
                            items={[
                                {
                                    key: 'contacts',
                                    label: `Contacts (${contactsPagination.total || 0})`,
                                    children: (
                                        <>
                                            <div style={compactStyles.controlsRow}>
                                                <Input
                                                    placeholder="Search contacts..."
                                                    style={{ width: 260 }}
                                                    value={localSearch}
                                                    onChange={e => setLocalSearch(e.target.value)}
                                                    allowClear
                                                    size="small"
                                                    prefix={<SearchOutlined style={{ color: 'var(--gh-text-secondary, #607D8B)' }} />}
                                                />
                                                <Select
                                                    value={contactsFilters.status}
                                                    style={{ width: 160 }}
                                                    onChange={val => setContactsFilters({ status: val })}
                                                    size="small"
                                                >
                                                    <Option value="all">All Status</Option>
                                                    <Option value="sent">Sent/Delivered</Option>
                                                    <Option value="delivered">Delivered</Option>
                                                    <Option value="failed">Failed</Option>
                                                </Select>
                                            </div>
                                            <Table
                                                className="gh-themed-table"
                                                columns={columns}
                                                dataSource={campaignContacts}
                                                rowKey="contact_id"
                                                loading={contactsLoading}
                                                size="small"
                                                onRow={(record) => ({
                                                    onClick: () => handleContactRowClick(record),
                                                    style: { cursor: 'pointer' },
                                                })}
                                                pagination={{
                                                    current: contactsPagination.page,
                                                    pageSize: contactsPagination.pageSize,
                                                    total: contactsPagination.total,
                                                    showSizeChanger: true,
                                                    size: 'small',
                                                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`
                                                }}
                                                onChange={handleTableChange}
                                            />
                                        </>
                                    )
                                },
                                {
                                    key: 'enrollment',
                                    label: 'Enrollment Progress',
                                    children: (
                                        <FlowEnrollmentBoard
                                            modulegroupId={campaignDetails.modulegroup_id}
                                            campaignId={campaignId}
                                            flowSnapshot={campaignDetails.flow_snapshot}
                                            moduleResponseOverrides={campaignDetails.module_response_overrides}
                                            onCardClick={handleEnrollmentCardClick}
                                        />
                                    )
                                }
                            ]}
                        />
                    ) : (
                        <>
                            <div style={compactStyles.controlsRow}>
                                <Input
                                    placeholder="Search contacts..."
                                    style={{ width: 260 }}
                                    value={localSearch}
                                    onChange={e => setLocalSearch(e.target.value)}
                                    allowClear
                                    size="small"
                                    prefix={<SearchOutlined style={{ color: 'var(--gh-text-secondary, #607D8B)' }} />}
                                />
                                <Select
                                    value={contactsFilters.status}
                                    style={{ width: 160 }}
                                    onChange={val => setContactsFilters({ status: val })}
                                    size="small"
                                >
                                    <Option value="all">All Status</Option>
                                    <Option value="sent">Sent/Delivered</Option>
                                    <Option value="delivered">Delivered</Option>
                                    <Option value="failed">Failed</Option>
                                </Select>
                            </div>
                            <Table
                                className="gh-themed-table"
                                columns={columns}
                                dataSource={campaignContacts}
                                rowKey="contact_id"
                                loading={contactsLoading}
                                size="small"
                                onRow={(record) => ({
                                    onClick: () => handleContactRowClick(record),
                                    style: { cursor: 'pointer' },
                                })}
                                pagination={{
                                    current: contactsPagination.page,
                                    pageSize: contactsPagination.pageSize,
                                    total: contactsPagination.total,
                                    showSizeChanger: true,
                                    size: 'small',
                                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`
                                }}
                                onChange={handleTableChange}
                            />
                        </>
                    )}
                </div>

                <CampaignContactDrawer
                    open={!!drawerContactId}
                    onClose={handleDrawerClose}
                    contactId={drawerContactId}
                    enrollment={drawerEnrollment}
                    modulegroupId={campaignDetails.modulegroup_id}
                    campaignId={campaignId}
                    modules={modules}
                    flowSnapshot={campaignDetails.flow_snapshot}
                    moduleResponseOverrides={campaignDetails.module_response_overrides}
                />
            </Content>
        </Layout>
    );
};

export default CampaignViewOngoing;
